import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import fonts from '../../design-system/fonts';
import colors from '../../design-system/colors';

export const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${colors.porcelain};
`;

export const Container = styled(motion.div)`
  flex: 1;
  padding: 120px 40px 80px;
  margin: 0 auto;
  width: 100%;
  max-width: 1400px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 100px 20px 60px;
  }
`;

export const BackButton = styled(motion.button)`
  background: none;
  border: none;
  font-size: 16px;
  color: ${colors.deepCharcoal};
  opacity: 0.6;
  cursor: pointer;
  padding: 12px 0;
  margin-bottom: 40px;
  font-family: ${fonts.body};
  transition: all 0.3s ease;

  &:hover {
    color: ${colors.sapphire};
    opacity: 1;
  }
`;

export const PageHeader = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

export const HeaderLeft = styled.div`
  flex: 1;
`;

export const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 40px;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.deepCharcoal};
  opacity: 0.5;
  font-family: ${fonts.body};
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

export const BreadcrumbLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background: ${colors.sapphire};
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${colors.sapphire};
    opacity: 1;
    &::after {
      width: 100%;
    }
  }
`;

export const BreadcrumbSeparator = styled.span`
  color: ${colors.goldenBronze};
  font-weight: 300;
  font-size: 18px;
`;

export const BreadcrumbCurrent = styled.span`
  font-weight: 700;
  opacity: 1;
  color: ${colors.deepCharcoal};
`;

export const CategoryTitle = styled(motion.h1)`
  font-size: 48px;
  font-weight: 900;
  color: ${colors.sapphire};
  margin: 0 0 10px 0;
  text-transform: uppercase;
  font-family: ${fonts.headline};
  letter-spacing: -0.03em;
  line-height: 1.1;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 80px;
    height: 4px;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

export const ResultCount = styled(motion.p)`
  font-size: 14px;
  color: ${colors.deepCharcoal};
  opacity: 0.6;
  margin: 0;
  font-family: ${fonts.body};
`;

export const ContentWrapper = styled(motion.div)`
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 60px;

  @media (max-width: 1024px) {
    grid-template-columns: 200px 1fr;
    gap: 40px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

export const Sidebar = styled(motion.aside)`
  @media (max-width: 768px) {
    order: 2;
  }
`;

export const SidebarText = styled(motion.p)`
  font-size: 16px;
  color: ${colors.deepCharcoal};
  line-height: 1.75;
  margin: 0 0 30px 0;
  font-family: ${fonts.body};
  font-weight: 500;
`;

export const CategoryList = styled(motion.ul)`
  padding: 0;
  margin: 0;
  list-style-type: none; /* We will use dots on the li */
`;

export const CategoryListItem = styled(motion.li)`
  margin-bottom: 12px;
  font-size: 14px;
  color: gray;
  font-family: ${fonts.body};
  position: relative;
  padding-left: 20px;
  transition: all 0.3s ease;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: gray;
    font-size: 18px;
    line-height: 1;
    top: 50%;
    transform: translateY(-50%);
    &:hover {
      color: ${colors.sapphire};
    }
  }

  &:hover {
    color: ${colors.sapphire};
    transform: translateX(5px);
  }
`;

export const CategoryListWrapper = styled.div`
  margin-bottom: 30px;
`;

export const ProductsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 20px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }
  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 16px;
  }
`;

export const ProductCard = styled(motion.div)`
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: visible;
  text-decoration: none;
  color: inherit;
  background: transparent;
  transition: all 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
  height: 100%;

  &:hover {
    transform: translateY(-8px);
  }
`;

export const ProductImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  padding: 0;
  flex-shrink: 0;
`;

export const ProductImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  display: block;
  transition: transform 0.6s cubic-bezier(0.25, 0.1, 0.25, 1), opacity 0.3s ease;

  ${ProductCard}:hover & {
    transform: scale(1.1);
  }
`;

export const ProductOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(17, 24, 39, 0.6) 0%,
    rgba(17, 24, 39, 0.3) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${ProductCard}:hover & {
    opacity: 1;
  }
`;

export const ProductArrow = styled(motion.span)`
  font-size: 36px;
  color: #ffffff;
  font-weight: 300;
  transform: translateX(-20px);
  transition: transform 0.3s ease;

  ${ProductCard}:hover & {
    transform: translateX(0);
  }
`;

export const SaleBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: ${colors.goldenBronze};
  color: ${colors.porcelain};
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  font-family: ${fonts.body};
  z-index: 1;
`;

export const ProductName = styled.h3`
  font-size: 20px;
  color: gray;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 24px 0px;
  text-align: left;
  margin: 0;
  font-family: ${fonts.body};
  transition: color 0.3s ease;
  position: relative;
  z-index: 1;

  ${ProductCard}:hover & {
    color: ${colors.sapphire};
  }

  @media (max-width: 1024px) {
    font-size: 18px;
  }

  @media (max-width: 768px) {
    padding: 20px 0px;
    font-size: 16px;
  }
`;

export const ProductPrice = styled.div`
  margin-bottom: 20px;
`;

export const Price = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.deepCharcoal};
  font-family: ${fonts.body};
`;

export const OriginalPrice = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.deepCharcoal};
  opacity: 0.5;
  text-decoration: line-through;
  margin-right: 12px;
  font-family: ${fonts.body};
`;

export const SalePrice = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.sapphire};
  font-family: ${fonts.body};
`;
